import { useParams, Link, Navigate } from "react-router-dom";
import { useState } from "react";
import { CATEGORIES, SITE } from "@/lib/seoData";
import SeoHead, {
  orgSchema, websiteSchema, breadcrumbSchema, faqSchema, serviceSchema
} from "@/components/SeoHead";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import FundingStrip from "@/components/FundingStrip";
import LeadModal from "@/components/LeadModal";
import HiBehindAi from "@/components/HiBehindAi";
import RepaymentCalculator from "@/components/RepaymentCalculator";
import { Button } from "@/components/ui/button";
import { ArrowRight, Check, Phone, ShieldCheck, Zap } from "lucide-react";

const rich = (text) =>
  String(text)
    .split(/(\[[^\]]+\]\([^)]+\))/g)
    .map((part, i) => {
      const m = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (!m) return part;
      return (
        <Link key={i} to={m[2]} className="text-[#E50914] underline underline-offset-4 decoration-[#E50914]/40 hover:text-white transition-colors">
          {m[1]}
        </Link>
      );
    });

export default function FinanceCategoryPage() {
  const { slug } = useParams();
  const cat = CATEGORIES.find((c) => c.slug === slug);
  const [open, setOpen] = useState(false);
  const [initialAmount, setInitialAmount] = useState("");

  if (!cat) return <Navigate to="/" replace />;

  const path = `/finance/${cat.slug}`;
  const title = cat.metaTitle || `${cat.title} Australia | ${SITE.name}`;
  const description = cat.metaDescription || `${cat.short} Explore ${cat.title.toLowerCase()} across banks, non-banks and private capital on the ${SITE.name} panel — ACL #${SITE.acl}.`;

  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Finance", path: "/" },
    { name: cat.title, path },
  ];

  const schemas = [
    orgSchema(),
    websiteSchema(),
    breadcrumbSchema(breadcrumbs),
    ...(cat.faqs?.length ? [faqSchema(cat.faqs)] : []),
    serviceSchema({ name: cat.title, description: cat.short, slug: cat.slug }),
  ];

  // Evergreen funding-class overview (qualitative — no rates, fees, LVRs or lender names)
  const fundingClasses = [
    ["Major bank", "Lower-cost long-term debt where policy and servicing fit", "Full financials", "Typically several weeks"],
    ["Second-tier bank", "Broader credit appetite than some major banks", "Full or alt-doc", "Often a few weeks"],
    ["Non-bank", "Flexible servicing and property assessment", "Alt-doc / lease-doc considered", "Often faster than banks"],
    ["Private credit", "Speed and flexibility for complex or time-critical scenarios", "Security- and exit-led", "Can move quickly, subject to due diligence"],
  ];

  const openWith = (amount) => {
    setInitialAmount(amount || "");
    setOpen(true);
  };

  return (
    <div className="bg-[#050505] text-white font-outfit min-h-screen">
      <SeoHead title={title} description={description} path={path} schemas={schemas} />
      <SiteHeader />

      {/* Hero */}
      <section className="px-6 pt-16 pb-16 sm:pt-24 max-w-6xl mx-auto">
        <nav className="text-xs text-zinc-500 mb-6 flex items-center gap-1.5" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-white">Home</Link>
          <span>/</span>
          <span className="text-zinc-300">{cat.title}</span>
        </nav>

        <p className="text-xs uppercase tracking-[0.4em] text-[#E50914] mb-4">Finance · Australia</p>
        <h1 className="font-bebas text-5xl sm:text-6xl md:text-7xl tracking-tight leading-[0.95] max-w-4xl">
          {cat.h1}
        </h1>
        <p className="text-zinc-400 text-lg mt-5 max-w-2xl leading-relaxed">{cat.short}</p>

        <div className="flex flex-wrap items-center gap-3 mt-8">
          <Button onClick={() => openWith("")} className="bg-[#E50914] hover:bg-[#F6121D] rounded-full h-12 px-6 font-semibold">
            Request a Human Review <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
          <a href={`tel:${SITE.phone}`} className="inline-flex items-center gap-2 h-12 px-5 rounded-full border border-zinc-800 hover:border-zinc-600 text-white">
            <Phone className="w-4 h-4" /> {SITE.phoneDisplay}
          </a>
        </div>

        <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl">
          {cat.maxLvr && <Stat label="Typical max LVR" value={`Up to ${cat.maxLvr}%`} />}
          <Stat label="Term" value={cat.term} />
          <Stat label="Funding" value="Banks · Non-banks · Private" />
          <Stat label="Indicative" value="24-hr answers" />
        </div>
      </section>

      {/* AI-Overview / featured-snippet quick answer */}
      {cat.quickAnswer && (
        <section className="border-t border-zinc-900 px-6 py-10 max-w-6xl mx-auto">
          <div className="rounded-2xl border border-[#E50914]/30 bg-gradient-to-br from-[#140709] via-[#0a0a0a] to-[#050505] p-6 sm:p-8 max-w-4xl">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#E50914] mb-3 font-semibold">Quick answer</p>
            <p className="text-zinc-200 text-base sm:text-lg leading-relaxed">{cat.quickAnswer}</p>
          </div>
        </section>
      )}

      {/* Benefits */}
      <section className="border-t border-zinc-900 px-6 py-16 max-w-6xl mx-auto">
        <h2 className="font-bebas text-3xl sm:text-4xl tracking-tight mb-8">Why {cat.title} with {SITE.name}</h2>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3">
          {cat.benefits.map((b) => (
            <li key={b} className="flex items-start gap-3 text-zinc-300">
              <Check className="w-4 h-4 text-[#E50914] mt-1 shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Deep editorial sections */}
      {cat.sections?.length > 0 && (
        <section className="border-t border-zinc-900 px-6 py-16 max-w-4xl mx-auto" data-testid="deep-sections">
          {cat.sections.map((sec) => (
            <div key={sec.heading} className="mb-16 last:mb-0">
              <h2 className="font-bebas text-3xl sm:text-4xl tracking-tight mb-5">{sec.heading}</h2>
              {sec.answer && (
                <div className="rounded-2xl border border-[#E50914]/30 bg-gradient-to-br from-[#140709] via-[#0a0a0a] to-[#050505] p-5 sm:p-6 mb-6">
                  <p className="text-zinc-200 leading-relaxed">{rich(sec.answer)}</p>
                </div>
              )}
              {sec.paras?.map((p, i) => (
                <p key={i} className="text-zinc-400 leading-relaxed mb-4">{rich(p)}</p>
              ))}
              {sec.bullets && (
                <ul className="space-y-2.5 my-5">
                  {sec.bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-3 text-zinc-300">
                      <Check className="w-4 h-4 text-[#E50914] mt-1 shrink-0" />
                      <span>{rich(b)}</span>
                    </li>
                  ))}
                </ul>
              )}
              {sec.example && (
                <div className="bg-[#0c0c0c] border border-zinc-800 rounded-2xl overflow-hidden my-6" data-testid="net-proceeds-example">
                  <div className="px-4 py-3 border-b border-zinc-800 text-[10px] uppercase tracking-[0.3em] text-[#E50914] font-semibold">{sec.example.title}</div>
                  <table className="w-full text-sm">
                    <tbody>
                      {sec.example.rows.map(([label, value], i) => (
                        <tr key={i} className={`border-b border-zinc-900 last:border-0 ${i === sec.example.rows.length - 1 ? "bg-[#140709]" : ""}`}>
                          <td className="px-4 py-3 text-zinc-300">{label}</td>
                          <td className={`px-4 py-3 text-right font-semibold whitespace-nowrap ${i === sec.example.rows.length - 1 ? "text-[#E50914]" : "text-white"}`}>{value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {sec.pull && (
                <blockquote className="border-l-2 border-[#E50914] pl-5 my-6 text-zinc-200 text-lg leading-relaxed italic">{sec.pull}</blockquote>
              )}
              {sec.note && <p className="text-xs text-zinc-600 italic mt-3">{sec.note}</p>}
            </div>
          ))}
        </section>
      )}

      {/* Calculator + funding-class overview */}
      {!cat.pillar && (
      <section className="border-t border-zinc-900 px-6 py-16 max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3">
          <RepaymentCalculator
            defaultTerm={cat.slug.includes("car") || cat.slug.includes("equipment") || cat.slug.includes("truck") ? 5 : 25}
            defaultAmount={cat.slug.includes("car") ? 50000 : cat.slug.includes("equipment") ? 100000 : 500000}
            onApply={({ loan_amount }) => openWith(loan_amount)}
          />
        </div>
        <div className="lg:col-span-2">
          <h3 className="font-bebas text-2xl sm:text-3xl tracking-wide mb-4">{cat.takeoutRoutes ? "Take-out routes" : "Funding classes"}</h3>
          {cat.takeoutRoutes ? (
            <>
              <div className="bg-[#0a0a0a] border border-zinc-800 rounded-2xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#0c0c0c] border-b border-zinc-800 text-zinc-500 uppercase text-[10px] tracking-widest">
                    <tr>
                      <th className="px-3 py-3 text-left font-medium">Take-out route</th>
                      <th className="px-3 py-3 text-left font-medium">Typical strengths</th>
                      <th className="px-3 py-3 text-left font-medium">Indicative timeframe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cat.takeoutRoutes.map(([route, strengths, timeframe]) => (
                      <tr key={route} className="border-b border-zinc-900 last:border-0 align-top">
                        <td className="px-3 py-3 text-white whitespace-nowrap">{route}</td>
                        <td className="px-3 py-3 text-zinc-300">{strengths}</td>
                        <td className="px-3 py-3 text-zinc-400">{timeframe}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-zinc-600 mt-3 leading-relaxed">
                Leverage and approval timeframes vary by lender, security, LVR, borrower profile, valuation, documentation and transaction complexity.
              </p>
            </>
          ) : (
            <>
              <div className="bg-[#0a0a0a] border border-zinc-800 rounded-2xl overflow-hidden" data-testid="funding-class-table">
                <table className="w-full text-sm">
                  <thead className="bg-[#0c0c0c] border-b border-zinc-800 text-zinc-500 uppercase text-[10px] tracking-widest">
                    <tr>
                      <th className="px-3 py-3 text-left font-medium">Funding class</th>
                      <th className="px-3 py-3 text-left font-medium">Typical use</th>
                      <th className="px-3 py-3 text-left font-medium">Indicative speed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fundingClasses.map(([klass, use, docs, speed]) => (
                      <tr key={klass} className="border-b border-zinc-900 last:border-0 align-top">
                        <td className="px-3 py-3 text-white whitespace-nowrap">{klass}</td>
                        <td className="px-3 py-3 text-zinc-300">{use}</td>
                        <td className="px-3 py-3 text-zinc-400">{speed}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-zinc-600 mt-3 leading-relaxed">
                General guidance only — not current pricing or a lender offer. Actual rates, leverage, documentation and timeframes vary by lender, security, borrower profile and transaction, and are confirmed on assessment.
              </p>
            </>
          )}
        </div>
      </section>
      )}

      {/* Trust strip */}
      <section className="border-t border-zinc-900 px-6 py-16 max-w-6xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { icon: ShieldCheck, t: "ACL #" + SITE.acl, d: "Australian Credit Licence holder, AFCA member." },
            { icon: Zap, t: "24-hr indicative", d: "Most enquiries get a same-day or next-day answer." },
            { icon: Check, t: "Banks, non-banks & private", d: "Banks, non-banks and private credit on one panel." },
          ].map((t) => (
            <div key={t.t} className="bg-[#0c0c0c] border border-zinc-800 rounded-2xl p-6">
              <t.icon className="w-5 h-5 text-[#E50914] mb-4" />
              <h3 className="font-semibold">{t.t}</h3>
              <p className="text-zinc-500 text-sm mt-1.5">{t.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-zinc-900 px-6 py-16 max-w-3xl mx-auto">
        <h2 className="font-bebas text-3xl sm:text-4xl tracking-tight mb-8">Frequently asked</h2>
        <div className="space-y-4">
          {[...(cat.faqs || []), ...(cat.visibleFaqs || [])].map(([q, a]) => (
            <details key={q} className="group bg-[#0c0c0c] border border-zinc-800 rounded-xl p-5 open:border-[#E50914]/40">
              <summary className="cursor-pointer list-none font-medium flex justify-between items-center">
                {q}
                <span className="text-[#E50914] group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="text-zinc-400 text-sm mt-3 leading-relaxed">{a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Internal links */}
      <section className="border-t border-zinc-900 px-6 py-16 max-w-6xl mx-auto">
        <h2 className="font-bebas text-2xl sm:text-3xl tracking-wide mb-6 text-zinc-300">Related finance</h2>
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.filter((c) => c.slug !== cat.slug && !c.pillar).map((c) => (
            <Link
              key={c.slug}
              to={`/finance/${c.slug}`}
              className="text-sm bg-[#0c0c0c] border border-zinc-800 hover:border-[#E50914]/50 rounded-full px-4 py-2 text-zinc-400 hover:text-white transition-colors"
            >
              {c.title}
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-zinc-900 px-6 py-20 text-center" data-testid="category-closing-cta">
        {cat.pillar && <HiBehindAi size="small" className="inline-block mb-4" testid="category-hi-behind-ai" />}
        <h2 className="font-bebas text-4xl sm:text-5xl tracking-tight">Ready to explore {cat.title.toLowerCase()}?</h2>
        <p className="text-zinc-500 mt-3 max-w-xl mx-auto text-sm">Our finance professionals review your requirements and objectives and discuss the options worth pursuing with you.</p>
        <Button onClick={() => openWith("")} className="mt-7 bg-[#E50914] hover:bg-[#F6121D] rounded-full h-14 px-8 font-semibold">
          Request a Human Review
        </Button>
      </section>

      <FundingStrip />
      <SiteFooter />
      <LeadModal open={open} onOpenChange={setOpen} initialAmount={initialAmount} initialLoanType={cat.title} sourceChannel="finance_page" />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-[#0c0c0c] border border-zinc-800 rounded-xl p-3">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-bebas text-xl sm:text-2xl tracking-wide text-white mt-1">{value}</div>
    </div>
  );
}
